/**
 * 合作伙伴全局状态管理 Store
 * 聚合 Partner、DashboardStats、Certification[]、Announcements[]、Activities[]
 * 通过 api 获取数据，不直接 import Mock 数据文件
 */
import { create } from 'zustand';
import type { Partner } from '@/types/partner';
import type { DashboardStats, PolicyAnnouncement, MarketActivity } from '@/types/dashboard';
import type { Certification, TechCertificationVO, CertCatalogVO } from '@/types/certification';
import type { CertCategory, CertItemStatus } from '@/types/common';
import { api } from '@/api';
import { serviceApi } from '@/api';

/** 后端 status 映射到前端 CertItemStatus */
function mapStatus(backendStatus: string): CertItemStatus {
  switch (backendStatus) {
    case 'approved':
      return 'approved';
    case 'rejected':
      return 'rejected';
    case 'pending':
    case 'reviewing':
      return 'pending';
    default:
      return 'pending';
  }
}

/** 后端 TechCertificationVO 映射为前端 Certification */
function mapTechCertToCertification(vo: TechCertificationVO, category: CertCategory = 'network'): Certification {
  return {
    id: vo.id,
    partnerId: vo.partnerId,
    category,
    name: vo.certName,
    issuer: vo.certOrg,
    obtainDate: vo.obtainDate || null,
    validUntil: vo.validUntil || null,
    status: mapStatus(vo.status),
    certificateUrl: vo.certUrl || undefined,
    createdAt: vo.createTime,
    updatedAt: vo.createTime,
  };
}

/**
 * 代理等级 → 默认星级映射（后端未返回 agentLevelStars，按等级推断）
 * diamond=5 / gold=4 / silver=3 / bronze=2
 */
function deriveAgentLevelStars(level: Partner['agentLevel'] | string | undefined): number {
  switch (level) {
    case 'diamond': return 5;
    case 'gold':    return 4;
    case 'silver':  return 3;
    case 'bronze':  return 2;
    default:        return 0;
  }
}

/** 合作伙伴 Store 接口 */
interface PartnerStore {
  /** 合作伙伴信息 */
  partnerInfo: Partner | null;
  /** 仪表盘统计数据 */
  dashboardStats: DashboardStats | null;
  /** 认证资质列表 */
  certifications: Certification[];
  /** 政策公告列表 */
  announcements: PolicyAnnouncement[];
  /** 市场活动列表 */
  activities: MarketActivity[];
  /** 后台认证目录（所有可申请的认证） */
  certCatalog: CertCatalogVO[];
  /** 我已提交的技术认证申请（按 certName 索引） */
  myTechApplications: Record<string, TechCertificationVO>;
  /** 加载状态 */
  loading: boolean;
  /** 错误信息 */
  error: string | null;
  /** 标识全局预加载是否已成功完成（避免路由切换时重复请求） */
  globalLoaded: boolean;

  /** 获取合作伙伴信息（仅在未加载时请求；如已加载过则直接返回缓存） */
  fetchPartnerInfo: (force?: boolean) => Promise<void>;
  /** 获取仪表盘统计数据 */
  fetchDashboardStats: (force?: boolean) => Promise<void>;
  /** 获取认证资质列表 */
  fetchCertifications: (category?: string) => Promise<void>;
  /** 新增技术认证 */
  addCertification: (data: TechCertificationVO) => Promise<void>;
  /** 获取后台认证目录（所有可申请的认证） */
  fetchCertCatalog: (force?: boolean) => Promise<void>;
  /** 获取我的技术认证申请列表 */
  fetchMyTechApplications: (force?: boolean) => Promise<void>;
  /** 获取政策公告列表 */
  fetchAnnouncements: (force?: boolean) => Promise<void>;
  /** 获取市场活动列表 */
  fetchActivities: (force?: boolean) => Promise<void>;
  /** 续期认证资质 */
  renewCertification: (certId: string) => Promise<void>;
  /** 全局预加载（在 AppShell 启动时调用一次） */
  preloadGlobal: () => Promise<void>;
}

/** 合作伙伴 Store */
export const usePartnerStore = create<PartnerStore>((set, get) => ({
  partnerInfo: null,
  dashboardStats: null,
  certifications: [],
  announcements: [],
  activities: [],
  certCatalog: [],
  myTechApplications: {},
  loading: false,
  error: null,
  globalLoaded: false,

  fetchPartnerInfo: async (force = false) => {
    if (!force && get().partnerInfo) return;
    set({ loading: true, error: null });
    try {
      const vo = await api.getPartnerInfo();
      // 将后端 PartnerProfileVO（已合并业务状态字段）适配为前端 Partner
      const partnerInfo: Partner = {
        partnerId: vo.partnerId,
        companyName: vo.companyName,
        agentCode: vo.agentCode || '',
        creditCode: vo.creditCode ?? undefined,
        legalRepresentative: vo.legalRepresentative ?? undefined,
        registeredAddress: vo.registeredAddress ?? undefined,
        region: vo.region ?? undefined,
        contactPhone: vo.contactPhone,
        contactEmail: vo.contactEmail,
        description: vo.description ?? undefined,
        // 业务状态字段（来自后端 /api/partner/profile）
        agentLevel: (vo.agentLevel as Partner['agentLevel']) || 'bronze',
        // 后端未返回 agentLevelStars，根据 agentLevel 推断 5 星映射
        agentLevelStars: deriveAgentLevelStars(vo.agentLevel as Partner['agentLevel']),
        serviceStars: vo.serviceStars ?? 0,
        cooperationYears: vo.cooperationYears ?? 0,
        certificationStatus: (vo.certificationStatus as Partner['certificationStatus']) || 'pending',
        certificationStatusName: vo.certificationStatusName || '',
        profileCompleted: vo.profileCompleted ?? false,
        validUntil: vo.validUntil || '',
      };
      set({ partnerInfo, loading: false });
    } catch (err) {
      set({ loading: false, error: (err as Error).message });
    }
  },

  fetchDashboardStats: async (force = false) => {
    if (!force && get().dashboardStats) return;
    set({ loading: true, error: null });
    try {
      const dashboardStats = await api.getDashboardStats();
      set({ dashboardStats, loading: false });
    } catch (err) {
      set({ loading: false, error: (err as Error).message });
    }
  },

  fetchCertifications: async (category?: string) => {
    set({ loading: true, error: null });
    try {
      const categoryMapped: CertCategory = (category as CertCategory) || 'network';
      const result = await serviceApi.getTechCertifications({
        page: 1,
        pageSize: 100,
      });
      const certifications: Certification[] = (result.list || []).map((vo) =>
        mapTechCertToCertification(vo, categoryMapped)
      );
      set({ certifications, loading: false });
    } catch (err) {
      set({ loading: false, error: (err as Error).message });
    }
  },

  addCertification: async (data: TechCertificationVO) => {
    set({ loading: true, error: null });
    try {
      await serviceApi.addTechCertification(data);
      // 新增成功后重新获取列表
      const result = await serviceApi.getTechCertifications({
        page: 1,
        pageSize: 100,
      });
      const certifications: Certification[] = (result.list || []).map((vo) =>
        mapTechCertToCertification(vo)
      );
      set({ certifications, loading: false });
    } catch (err) {
      set({ loading: false, error: (err as Error).message });
    }
  },

  fetchCertCatalog: async (force = false) => {
    if (!force && get().certCatalog.length > 0) return;
    set({ loading: true, error: null });
    try {
      const list = await serviceApi.getCertCatalog();
      set({ certCatalog: list || [], loading: false });
    } catch (err) {
      set({ loading: false, error: (err as Error).message });
    }
  },

  fetchMyTechApplications: async (force = false) => {
    if (!force && Object.keys(get().myTechApplications).length > 0) return;
    set({ loading: true, error: null });
    try {
      // 拉自己所有的 tech-certifications 申请（多页拉满）
      const result = await serviceApi.getTechCertifications({
        page: 1,
        pageSize: 100,
      });
      const map: Record<string, TechCertificationVO> = {};
      (result.list || []).forEach((vo) => {
        if (vo.certName) map[vo.certName] = vo;
      });
      set({ myTechApplications: map, loading: false });
    } catch (err) {
      set({ loading: false, error: (err as Error).message });
    }
  },

  fetchAnnouncements: async (force = false) => {
    if (!force && get().announcements.length > 0) return;
    set({ loading: true, error: null });
    try {
      const announcements = await api.getAnnouncements();
      set({ announcements, loading: false });
    } catch (err) {
      set({ loading: false, error: (err as Error).message });
    }
  },

  fetchActivities: async (force = false) => {
    if (!force && get().activities.length > 0) return;
    set({ loading: true, error: null });
    try {
      const activities = await api.getActivities();
      set({ activities, loading: false });
    } catch (err) {
      set({ loading: false, error: (err as Error).message });
    }
  },

  renewCertification: async (certId: string) => {
    set({ loading: true, error: null });
    try {
      await api.renewCertification(certId);
      // 续期成功后更新认证列表中对应项的状态为 approved
      set((state) => ({
        certifications: state.certifications.map((cert) =>
          cert.id === certId
            ? {
                ...cert,
                status: 'approved' as const,
                validUntil: cert.validUntil
                  ? new Date(
                      new Date(cert.validUntil).getTime() + 365 * 24 * 60 * 60 * 1000,
                    ).toISOString()
                  : new Date(
                      Date.now() + 365 * 24 * 60 * 60 * 1000,
                    ).toISOString(),
              }
            : cert,
        ),
        loading: false,
      }));
    } catch (err) {
      set({ loading: false, error: (err as Error).message });
    }
  },

  /**
   * 全局预加载：只在 App 启动/登入时调用一次。
   * 后续路由切换不再触发这些接口（受 globalLoaded / 缓存命中保护）。
   */
  preloadGlobal: async () => {
    if (get().globalLoaded) return;
    set({ loading: true, error: null });
    try {
      const [partnerProfileVO, dashboardStats, announcements, activities] = await Promise.all([
        api.getPartnerInfo(),
        api.getDashboardStats(),
        api.getAnnouncements(),
        api.getActivities(),
      ]);
      const partnerInfo: Partner = {
        partnerId: partnerProfileVO.partnerId,
        companyName: partnerProfileVO.companyName,
        agentCode: partnerProfileVO.agentCode || '',
        creditCode: partnerProfileVO.creditCode ?? undefined,
        legalRepresentative: partnerProfileVO.legalRepresentative ?? undefined,
        registeredAddress: partnerProfileVO.registeredAddress ?? undefined,
        region: partnerProfileVO.region ?? undefined,
        contactPhone: partnerProfileVO.contactPhone,
        contactEmail: partnerProfileVO.contactEmail,
        description: partnerProfileVO.description ?? undefined,
        // 业务状态字段
        agentLevel: (partnerProfileVO.agentLevel as Partner['agentLevel']) || 'bronze',
        agentLevelStars: deriveAgentLevelStars(partnerProfileVO.agentLevel as Partner['agentLevel']),
        serviceStars: partnerProfileVO.serviceStars ?? 0,
        cooperationYears: partnerProfileVO.cooperationYears ?? 0,
        certificationStatus: (partnerProfileVO.certificationStatus as Partner['certificationStatus']) || 'pending',
        certificationStatusName: partnerProfileVO.certificationStatusName || '',
        profileCompleted: partnerProfileVO.profileCompleted ?? false,
        validUntil: partnerProfileVO.validUntil || '',
      };
      set({ partnerInfo, dashboardStats, announcements, activities, loading: false, globalLoaded: true });
    } catch (err) {
      set({ loading: false, error: (err as Error).message });
    }
  },
}));
